// src/app/pages/home/home.page.ts
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';

import { GsapService } from '../../core/gsap/gsap.service';
import { isBrowser } from '../../core/utils/platform';
import { buildWhatsAppLink } from '../../core/utils/wa';
import { scrollToId } from '../../core/utils/scroll';

type ServiceCard = {
  title: string;
  desc: string;
  bullets: string[];
  tag: string;
};

@Component({
  standalone: true,
  selector: 'kb-home-page',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './home.page.html',
  styleUrl: './home.page.scss'
})
export class HomePageComponent implements OnInit, OnDestroy {
  private gsapSvc = inject(GsapService);
  private fb = inject(FormBuilder);

  @ViewChild('root', { static: true }) rootRef!: ElementRef<HTMLElement>;
  // ✅ existe en tu HTML: <details class="navDrop" #navDrop>
  @ViewChild('navDrop', { static: false }) navDropRef?: ElementRef<HTMLDetailsElement>;

  year = new Date().getFullYear();

  nav = [
    { id: 'inicio', label: 'Inicio' },
    { id: 'servicios', label: 'Servicios' },
    { id: 'enfoque', label: 'Enfoque' },
    { id: 'practicas', label: 'Prácticas realizadas' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contacto', label: 'Contacto' }
  ];

  socials = {
    instagram: 'https://instagram.com/k.bentancor',
    whatsapp: buildWhatsAppLink('Hola Karen! Quiero consultar por una colocación / sesión.')
  };

  services: ServiceCard[] = [
    {
      title: 'Kinesio Tape',
      tag: 'Soporte + movimiento',
      desc: 'Vendaje neuromuscular terapéutico que acompaña tu recuperación sin inmovilizar.',
      bullets: [
        'Alivio del dolor muscular y articular',
        'Ayuda a reducir inflamación, edemas y hematomas',
        'Mejora la circulación sanguínea y linfática',
        'Soporte muscular sin limitar el movimiento',
        'Mejora postura y conciencia corporal',
        'Acompaña rehabilitación y rendimiento'
      ]
    },
    {
      title: 'Postura & conciencia corporal',
      tag: 'Movimiento consciente',
      desc: 'Estímulo propioceptivo para mejorar postura, control y calidad del movimiento.',
      bullets: [
        'Mejora el patrón de movimiento',
        'Ayuda a corregir posturas sostenidas',
        'Soporte para entrenamiento y rutina diaria',
        'Ideal para bienestar general'
      ]
    },
    {
      title: 'Rehabilitación / recuperación',
      tag: 'Vuelta segura',
      desc: 'Acompañamiento en procesos de rehabilitación y lesiones deportivas.',
      bullets: [
        'Esguinces, tendinitis y sobrecargas',
        'Dolor cervical, lumbar y contracturas',
        'Plan de recuperación progresivo',
        'Indicaciones personalizadas'
      ]
    },
    {
      title: 'Bienestar (Pilates / actividad)',
      tag: 'Cuerpo en armonía',
      desc: 'Un enfoque suave y efectivo para moverte mejor, con soporte cuando lo necesitás.',
      bullets: [
        'Más conciencia corporal',
        'Menos tensión muscular',
        'Mejor recuperación entre sesiones',
        'Ideal para rutina saludable'
      ]
    }
  ];

  faqs = [
    {
      q: '¿El kinesio tape reemplaza una consulta?',
      a: 'No. Es un complemento. Su eficacia depende de una evaluación y colocación correctas, idealmente por un profesional capacitado.'
    },
    {
      q: '¿Se puede mojar?',
      a: 'Sí. Está pensado para uso prolongado y es resistente al agua (según el tipo de cinta y cuidado).'
    },
    {
      q: '¿Cuánto dura colocado?',
      a: 'Generalmente varios días. Depende de la piel, la zona y la actividad.'
    },
    {
      q: '¿Para quién es?',
      a: 'Dolores musculares, tendinitis, esguinces, lumbalgia/cervicalgia, fascitis plantar, edemas y corrección postural.'
    }
  ];

  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    phone: [''],
    message: ['', [Validators.required, Validators.minLength(6)]]
  });

  submitting = false;
  submitted = false;

  activeSection = 'inicio';

  // ============================================================
  // BACKGROUND FLASHES (JS elements + GSAP loop)
  // ============================================================
  private flashTl: any | null = null;
  private flashEls: HTMLElement[] = [];

  // ✅ para cerrar dropdown cuando click afuera / ESC
  private teardownDocHandlers: (() => void) | null = null;

  // ============================================================
  // PRÁCTICAS REALIZADAS - MODAL GALERÍA
  // ============================================================
  private readonly PRACTICES_COUNT = 15;
  private readonly PRACTICES_BASE_PATH = 'assets/img/'; // t1.jpeg ... t15.jpeg

  isPracticesOpen = false;
  viewerIndex: number | null = null;
  practiceImgs: string[] = [];
  private practicesModalTl: any | null = null;
  private viewerTl: any | null = null;
  private previousBodyOverflow = '';

  // ============================================================
  // BENEFICIOS - WIDGET FLOTANTE
  // ============================================================
  showBenefits = false;
  benefitsOpen = false;
  private benefitsShowTl: any | null = null;
  private benefitsToggleTl: any | null = null;

  benefits = [
    'Alivia el dolor',
    'Desinflama',
    'Mejora la movilidad y flexibilidad',
    'Ayuda a sostener',
    'Genera más confianza'
  ];

  // ============================================================
  // ✅ FIXES PARA TEMPLATE (evita arrow functions + casts en HTML)
  // ============================================================
  get activeSectionLabel(): string {
    return this.getNavLabel(this.activeSection) || 'Inicio';
  }

  getNavLabel(id: string): string {
    for (let i = 0; i < this.nav.length; i++) {
      if (this.nav[i].id === id) return this.nav[i].label;
    }
    return '';
  }

  goFromDropdown(id: string, ev?: Event): void {
    // Caso especial: prácticas abre modal
    if (id === 'practicas') {
      this.openPractices();
      this.activeSection = 'practicas';

      // cerrar details
      const target = (ev?.currentTarget as HTMLElement | null) ?? null;
      const details = (target?.closest('details') as HTMLDetailsElement | null) ?? null;
      if (details?.hasAttribute('open')) details.removeAttribute('open');
      return;
    }

    this.go(id);

    if (!isBrowser()) return;

    const target = (ev?.currentTarget as HTMLElement | null) ?? null;
    const details = (target?.closest('details') as HTMLDetailsElement | null) ?? null;
    if (details?.hasAttribute('open')) details.removeAttribute('open');
  }

  // ============================================================
  // LIFECYCLE
  // ============================================================
  ngOnInit(): void {
    if (!isBrowser()) return;

    this.practiceImgs = Array.from(
      { length: this.PRACTICES_COUNT },
      (_, i) => `${this.PRACTICES_BASE_PATH}t${i + 1}.jpeg`
    );

    const gsap = this.gsapSvc.ready();
    const root = this.rootRef.nativeElement;

    // ---------- Intro ----------
    const introTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    introTl.set(root.querySelectorAll('[data-reveal]'), { opacity: 0, y: 18 });
    introTl.set(root.querySelector('[data-hero-bg]'), { opacity: 0, scale: 1.04 });
    introTl.to(root.querySelector('[data-hero-bg]'), { opacity: 1, scale: 1, duration: 1.2 }, 0);
    introTl.to(
      root.querySelectorAll('[data-reveal]'),
      { opacity: 1, y: 0, duration: 0.9, stagger: 0.08 },
      0.15
    );

    // ---------- Gold button ----------
    const goldBtn = root.querySelector('.kb-btn--gold') as HTMLElement | null;
    if (goldBtn) {
      gsap.fromTo(
        goldBtn,
        { opacity: 0, y: 12, scale: 0.96, filter: 'blur(8px)' },
        { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out', delay: 0.3 }
      );

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.to(goldBtn, {
          filter: 'drop-shadow(0 0 12px rgba(245,208,106,0.4))',
          duration: 2.5,
          yoyo: true,
          repeat: -1,
          ease: 'sine.inOut'
        });
      }
    }

    // ---------- Floating blobs ----------
    const a1 = root.querySelector('.aurora--1');
    const a2 = root.querySelector('.aurora--2');
    const a3 = root.querySelector('.aurora--3');

    if (a1) {
      gsap.to(a1, { x: 120, y: -80, scale: 1.1, rotation: 5, duration: 18, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }
    if (a2) {
      gsap.to(a2, { x: -100, y: 60, scale: 1.15, rotation: -8, duration: 22, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }
    if (a3) {
      gsap.to(a3, { x: 80, y: -40, scale: 1.08, rotation: 3, duration: 25, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    }

    // ---------- Section reveals ----------
    root.querySelectorAll<HTMLElement>('[data-stagger]').forEach((section) => {
      const items = section.querySelectorAll<HTMLElement>('[data-item]');
      gsap.set(items, { opacity: 0, y: 18, filter: 'blur(6px)' });
      gsap.to(items, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.9,
        stagger: 0.08,
        scrollTrigger: {
          trigger: section,
          start: 'top 72%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      });
    });

    // ---------- Parallax ornaments ----------
    const oA = root.querySelector('.hero__ornament--a');
    const oB = root.querySelector('.hero__ornament--b');

    if (oA) {
      gsap.to(oA, {
        yPercent: -18,
        scrollTrigger: { trigger: '#inicio', start: 'top top', end: 'bottom top', scrub: true }
      });
    }
    if (oB) {
      gsap.to(oB, {
        yPercent: 22,
        scrollTrigger: { trigger: '#inicio', start: 'top top', end: 'bottom top', scrub: true }
      });
    }

    // ---------- Background flashes ----------
    this.mountFlashes(gsap, root);

    // ---------- Scrollspy ----------
    const ids = this.nav.map((n) => n.id).filter((id) => id !== 'practicas');
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        this.activeSection = (visible.target as HTMLElement).id;
      },
      { root: null, rootMargin: '-40% 0px -55% 0px', threshold: [0.08, 0.12, 0.2] }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    (this as any)._obs = obs;

    // ---------- Dropdown guards ----------
    this.installDropdownGuards();

    // ---------- Benefits widget scroll listener ----------
    this.initBenefitsScrollListener();
  }

  ngOnDestroy(): void {
    if (!isBrowser()) return;

    const obs: IntersectionObserver | undefined = (this as any)._obs;
    obs?.disconnect();

    // cleanup flashes
    try {
      this.flashTl?.kill?.();
    } catch {
      /* noop */
    }
    this.flashTl = null;
    this.flashEls.forEach((el) => el.remove());
    this.flashEls = [];

    // cleanup dropdown handlers
    try {
      this.teardownDocHandlers?.();
    } catch {
      /* noop */
    }
    this.teardownDocHandlers = null;

    // cleanup benefits scroll listener
    try {
      (this as any)._benefitsScrollCleanup?.();
    } catch {
      /* noop */
    }

    // cleanup modal timelines
    try {
      this.practicesModalTl?.kill?.();
      this.viewerTl?.kill?.();
      this.benefitsShowTl?.kill?.();
      this.benefitsToggleTl?.kill?.();
    } catch {
      /* noop */
    }

    this.restoreBodyScroll();
    this.gsapSvc.killAll();
  }

  // ============================================================
  // NAV + LINKS
  // ============================================================
  go(id: string): void {
    if (!isBrowser()) return;
    scrollToId(id, 88);
  }

  openWhatsApp(): void {
    if (!isBrowser()) return;
    window.open(this.socials.whatsapp, '_blank');
  }

  openInstagram(): void {
    if (!isBrowser()) return;
    window.open(this.socials.instagram, '_blank');
  }

  private buildContactMessage(): string {
    const v = this.form.getRawValue();

    const name = (v.name ?? '').trim() || 'Tu nombre';
    const phone = (v.phone ?? '').trim() || 'Tu teléfono';
    const message = (v.message ?? '').trim() || '¿Cuál es tu consulta?';

    return `Hola Karen! 👋
Nombre: ${name}
Teléfono: ${phone}
Mensaje: ${message}`;
  }

  submit(): void {
    if (!isBrowser()) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;

    const text = this.buildContactMessage();
    const link = buildWhatsAppLink(text);

    window.setTimeout(() => {
      this.submitting = false;
      this.submitted = true;
      window.open(link, '_blank');
      this.form.reset();
    }, 450);
  }

  // ============================================================
  // GLOBAL LISTENERS
  // ============================================================
  @HostListener('window:scroll')
  onScroll(): void {
    this.closeDropdown();
  }

  // ✅ Un SOLO listener de teclado (evita duplicados y rarezas)
  @HostListener('window:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (!isBrowser()) return;

    // ESC
    if (ev.key === 'Escape') {
      this.closeDropdown();

      if (this.isPracticesOpen) {
        if (this.viewerIndex !== null) {
          this.closeViewer();
        } else {
          this.closePractices();
        }
      }
      return;
    }

    // Flechas solo si viewer abierto
    if (this.viewerIndex !== null) {
      if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        this.nextImg();
      } else if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        this.prevImg();
      }
    }
  }

  // ============================================================
  // PRÁCTICAS - MODAL
  // ============================================================
  openPractices(): void {
    if (!isBrowser()) return;

    this.isPracticesOpen = true;
    this.viewerIndex = null;
    this.blockBodyScroll();

    const gsap = this.gsapSvc.ready();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Esperar a que Angular pinte el @if (overlay/modal)
    this.afterNextPaint(() => {
      const root = this.rootRef.nativeElement;
      const overlay = root.querySelector('.pmOverlay') as HTMLElement | null;
      const modal = root.querySelector('.pmModal') as HTMLElement | null;
      const thumbs = root.querySelectorAll('.pmThumb');

      if (!overlay || !modal) return;

      // matar timeline anterior si existía
      try {
        this.practicesModalTl?.kill?.();
      } catch {
        /* noop */
      }
      this.practicesModalTl = null;

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Overlay
      tl.set(overlay, { opacity: 0 });
      tl.set('.pmAurora1, .pmAurora2', { opacity: 0 });
      tl.to(overlay, { opacity: 1, duration: 0.25 }, 0);
      tl.to('.pmAurora1', { opacity: 0.3, duration: 0.4 }, 0.1);
      tl.to('.pmAurora2', { opacity: 0.2, duration: 0.4 }, 0.15);

      // Modal
      tl.set(modal, { opacity: 0, y: 24, scale: 0.96, filter: 'blur(10px)' });
      tl.to(modal, { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.65 }, 0.25);

      // Thumbs
      if (!reducedMotion && thumbs.length) {
        tl.set(thumbs, { opacity: 0, y: 10, scale: 0.98 });
        tl.to(thumbs, { opacity: 1, y: 0, scale: 1, duration: 0.4, stagger: 0.04 }, 0.45);
      } else if (thumbs.length) {
        tl.set(thumbs, { opacity: 0 });
        tl.to(thumbs, { opacity: 1, duration: 0.2, stagger: 0.02 }, 0.45);
      }

      this.practicesModalTl = tl;

      // Focus
      window.setTimeout(() => {
        const firstFocusable = modal.querySelector('.pmClose') as HTMLElement | null;
        firstFocusable?.focus();
      }, 120);
    });
  }

  closePractices(): void {
    if (!isBrowser()) return;

    const gsap = this.gsapSvc.ready();

    const root = this.rootRef.nativeElement;
    const overlay = root.querySelector('.pmOverlay') as HTMLElement | null;
    const modal = root.querySelector('.pmModal') as HTMLElement | null;
    const thumbs = root.querySelectorAll('.pmThumb');

    // Si todavía no está montado (raro), cerrá directo
    if (!overlay || !modal) {
      this.isPracticesOpen = false;
      this.viewerIndex = null;
      this.restoreBodyScroll();
      return;
    }

    // matar timeline anterior
    try {
      this.practicesModalTl?.kill?.();
    } catch {
      /* noop */
    }
    this.practicesModalTl = null;

    const exitTl = gsap.timeline({ defaults: { ease: 'power2.in' } });

    if (thumbs.length) {
      exitTl.to(thumbs, { opacity: 0, y: -10, scale: 0.98, duration: 0.15, stagger: 0.02 }, 0);
    }

    exitTl.to(modal, { opacity: 0, y: 10, scale: 0.98, filter: 'blur(8px)', duration: 0.4 }, 0.12);
    exitTl.to(overlay, { opacity: 0, duration: 0.2 }, 0.32);
    exitTl.to('.pmAurora1, .pmAurora2', { opacity: 0, duration: 0.15 }, 0.32);

    exitTl.eventCallback('onComplete', () => {
      this.isPracticesOpen = false;
      this.viewerIndex = null;
      this.restoreBodyScroll();
      this.practicesModalTl = null;
    });

    this.practicesModalTl = exitTl;
  }

  openViewer(index: number): void {
    if (!isBrowser()) return;

    // clamp safe
    const next = Math.max(0, Math.min(index, this.practiceImgs.length - 1));
    this.viewerIndex = next;

    const gsap = this.gsapSvc.ready();

    // Esperar a que el @if del viewer pinte
    this.afterNextPaint(() => {
      const root = this.rootRef.nativeElement;
      const viewer = root.querySelector('.practices-viewer') as HTMLElement | null;
      if (!viewer) return;

      // matar timeline anterior
      try {
        this.viewerTl?.kill?.();
      } catch {
        /* noop */
      }
      this.viewerTl = null;

      const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
      tl.set(viewer, { opacity: 0, scale: 0.95 });
      tl.to(viewer, { opacity: 1, scale: 1, duration: 0.3 });

      this.viewerTl = tl;
    });
  }

  closeViewer(): void {
    if (!isBrowser()) return;

    // Si no hay timeline, cerrar directo
    if (!this.viewerTl) {
      this.viewerIndex = null;
      return;
    }

    const tl = this.viewerTl;
    // reverse() no siempre devuelve Promise según builds/plugins -> hacemos callback seguro
    tl.eventCallback('onReverseComplete', () => {
      this.viewerIndex = null;
      this.viewerTl = null;
      tl.eventCallback('onReverseComplete', null);
    });
    tl.reverse();
  }

  nextImg(): void {
    if (this.viewerIndex === null) return;
    if (this.viewerIndex < this.practiceImgs.length - 1) {
      this.openViewer(this.viewerIndex + 1);
    }
  }

  prevImg(): void {
    if (this.viewerIndex === null) return;
    if (this.viewerIndex > 0) {
      this.openViewer(this.viewerIndex - 1);
    }
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target !== event.currentTarget) return;

    if (this.viewerIndex !== null) this.closeViewer();
    else this.closePractices();
  }

  // ============================================================
  // BENEFICIOS - WIDGET
  // ============================================================
  private initBenefitsScrollListener(): void {
    if (!isBrowser()) return;

    const onScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight * 0.8;

      if (scrollY > heroHeight && !this.showBenefits) {
        this.showBenefits = true;
        this.animateBenefitsEntry();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    (this as any)._benefitsScrollCleanup = () => window.removeEventListener('scroll', onScroll);
  }

  private animateBenefitsEntry(): void {
    if (!isBrowser()) return;

    const gsap = this.gsapSvc.ready();

    this.afterNextPaint(() => {
      const root = this.rootRef.nativeElement;
      const widget = root.querySelector('.benefitsFloat') as HTMLElement | null;
      if (!widget) return;

      try {
        this.benefitsShowTl?.kill?.();
      } catch {
        /* noop */
      }
      this.benefitsShowTl = null;

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.set(widget, { opacity: 0, y: 40, scale: 0.9 });
      tl.to(widget, { opacity: 1, y: 0, scale: 1, duration: 0.6 });

      this.benefitsShowTl = tl;
    });
  }

  toggleBenefits(): void {
    if (!isBrowser()) return;

    const gsap = this.gsapSvc.ready();
    const root = this.rootRef.nativeElement;

    const body = root.querySelector('.benefitsFloat__body') as HTMLElement | null;
    const icon = root.querySelector('.benefitsFloat__icon') as HTMLElement | null;

    if (!body || !icon) return;

    this.benefitsOpen = !this.benefitsOpen;

    try {
      this.benefitsToggleTl?.kill?.();
    } catch {
      /* noop */
    }
    this.benefitsToggleTl = null;

    const tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });

    if (this.benefitsOpen) {
      tl.set(body, { display: 'block' }); // ✅ evita flicker si venías con display none inline
      tl.to(body, { height: 'auto', opacity: 1, duration: 0.3 });
      tl.to(icon, { rotation: 180, duration: 0.3 }, 0);
    } else {
      tl.to(body, { height: 0, opacity: 0, duration: 0.3 });
      tl.to(icon, { rotation: 0, duration: 0.3 }, 0);
      tl.set(body, { display: 'none' }); // ✅ deja cerrado (tu HTML también lo controla)
    }

    this.benefitsToggleTl = tl;
  }

  // ============================================================
  // DROPDOWN HELPERS
  // ============================================================
  private closeDropdown(): void {
    if (!isBrowser()) return;
    const d = this.navDropRef?.nativeElement ?? null;
    if (d?.hasAttribute('open')) d.removeAttribute('open');
  }

  private installDropdownGuards(): void {
    if (!isBrowser()) return;

    const root = this.rootRef.nativeElement;
    const details =
      this.navDropRef?.nativeElement ?? (root.querySelector('.navDrop') as HTMLDetailsElement | null);

    if (!details) return;

    const onPointerDown = (ev: Event) => {
      const t = ev.target as Node | null;
      if (!t) return;
      if (details.contains(t)) return;
      if (details.hasAttribute('open')) details.removeAttribute('open');
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && details.hasAttribute('open')) {
        details.removeAttribute('open');
      }
    };

    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);

    this.teardownDocHandlers = () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }

  // ============================================================
  // FLASHES IMPLEMENTATION
  // ============================================================
  private mountFlashes(gsap: any, root: HTMLElement): void {
    const layer = root.querySelector<HTMLElement>('[data-flash-layer]');
    if (!layer) return;

    // Evita dobles montajes
    this.flashEls.forEach((el) => el.remove());
    this.flashEls = [];
    try {
      this.flashTl?.kill?.();
    } catch {
      /* noop */
    }
    this.flashTl = null;

    const COUNT = 10;

    // ✅ RGB base (sin alpha) + alpha aparte (evita strings inválidos)
    const RGB = [
      [182, 136, 255], // lila
      [120, 237, 217]  // verde agua
    ] as const;

    layer.style.pointerEvents = 'none';

    for (let i = 0; i < COUNT; i++) {
      const el = document.createElement('div');
      el.className = 'flash';
      el.setAttribute('aria-hidden', 'true');

      el.style.position = 'absolute';
      el.style.inset = 'auto';
      el.style.width = '220px';
      el.style.height = '220px';
      el.style.borderRadius = '999px';
      el.style.mixBlendMode = 'screen';
      el.style.filter = 'blur(18px)';
      el.style.opacity = '0';
      el.style.transform = 'translate3d(0,0,0)';

      layer.appendChild(el);
      this.flashEls.push(el);
    }

    const r = (min: number, max: number) => min + Math.random() * (max - min);

    const place = (el: HTMLElement) => {
      const rect = layer.getBoundingClientRect();
      const x = r(-0.1, 1.1) * rect.width;
      const y = r(-0.1, 1.1) * rect.height;
      gsap.set(el, { x, y });
    };

    this.flashEls.forEach(place);

    const master = gsap.timeline({ repeat: -1 });

    this.flashEls.forEach((el, idx) => {
      const dur = r(1.6, 3.2);
      const driftX = r(-180, 180);
      const driftY = r(-140, 140);
      const s0 = r(0.7, 1.1);
      const s1 = r(1.15, 1.65);
      const rot0 = r(-18, 18);
      const rot1 = rot0 + r(-28, 28);

      const pick = RGB[Math.floor(Math.random() * RGB.length)];
      const intensity = r(0.55, 0.95);

      const color = `rgba(${pick[0]}, ${pick[1]}, ${pick[2]}, ${intensity})`;
      el.style.background = `radial-gradient(circle at 30% 30%, ${color}, rgba(0,0,0,0) 62%)`;

      const t = gsap.timeline();
      t.call(() => place(el));

      t.fromTo(
        el,
        { opacity: 0, scale: s0, rotation: rot0 },
        { opacity: r(0.35, 0.75), scale: s1, rotation: rot1, duration: dur * 0.25, ease: 'power2.out' }
      );

      t.to(
        el,
        { x: `+=${driftX}`, y: `+=${driftY}`, duration: dur * 0.55, ease: 'sine.inOut' },
        dur * 0.1
      );

      t.to(
        el,
        { opacity: 0, scale: `+=${r(0.08, 0.22)}`, duration: dur * 0.35, ease: 'power2.in' },
        dur * 0.65
      );

      master.add(t, idx * r(0.18, 0.32));
    });

    master.to(layer, { opacity: 1, duration: 0.01 }, 0);

    // respiración suave del layer
    gsap.to(layer, { opacity: 1, duration: 1.2, repeat: -1, yoyo: true, ease: 'sine.inOut' });

    this.flashTl = master;
  }

  // ============================================================
  // UTILS
  // ============================================================
  private blockBodyScroll(): void {
    if (!isBrowser()) return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    if (!isBrowser()) return;
    document.body.style.overflow = this.previousBodyOverflow;
  }

  /** Corre “después del paint” para que existan nodos de @if/@for antes de animar */
  private afterNextPaint(cb: () => void): void {
    if (!isBrowser()) return;
    requestAnimationFrame(() => requestAnimationFrame(cb));
  }
}
